import React, { useMemo } from "react";
import { flatMap } from "lodash";
import {
    DriveGear,
} from "@optimusride/client-library/@types/grpc/vehicle_status";
import { renderToStaticMarkup } from "react-dom/server";
import { OperatingArea } from "@optimusride/client-library/@types/grpc/site";
import { injectGlobal } from "@emotion/css";
import {
    BLACK, WHITE,
} from "../../common/styles/colors";
import { BODY_FONT, FONT_0_875, FONT_1 } from "../../common/styles/fonts";
import { convertLatLng, radiansToDegree } from "../../common/utils";
import { defaultMarkerCalculator, splitVehicleID, SVG_BASE } from "../utils";
import useVehicleFirestore from "./useVehicleFirestore";
import VehicleMarker, { VEHICLE_COLOR_MAPPING } from "../assets/VehicleMarker";
import {
    Z_INDEX_VEHICLE,
    Z_INDEX_VEHICLE_LABEL,
} from "../../common/styles/z-index";
import {
    SPACING_1,
    SPACING_24,
    SPACING_44,
} from "../../common/styles/spacing";
// eslint-disable-next-line import/no-absolute-path
import VehicleCluster from "/vehicleCluster1.png";
import VehicleModalText from "../components/VehicleModalText";

injectGlobal(`
    .Cluster_Vehicle img {
        height: unset !important;
        width: 6em !important;
    }

    .Cluster_Vehicle span {
        position: absolute;
        bottom: calc(-1 * ${SPACING_44}) !important;
        right: ${SPACING_24} !important;
        margin-bottom: ${SPACING_1} !important;

        color: ${BLACK} !important;
        font-size: ${FONT_1} !important;
    }
`);

const ANGLE_OFFSET = 90;
// Custom method to render an svg with the right status color and pose
const VEHICLE_IMAGE = (angle: number, driveGear?: DriveGear) => {
    const driveGearColor = VEHICLE_COLOR_MAPPING[
        driveGear === undefined ? DriveGear.UNKNOWN : driveGear
    ];

    // the vehicle image is offset by 90 degree compared to what is returned from the vehicles
    return (SVG_BASE + encodeURIComponent(
        renderToStaticMarkup(
            <VehicleMarker
                height={40}
                width={40}
                angle={angle - ANGLE_OFFSET}
                vehicleStatusColor={driveGearColor}
            />,
        ),
    ));
};

// The black circle background for the label
const CIRCLE_IMAGE = SVG_BASE + encodeURIComponent(
    renderToStaticMarkup(
        <svg
            height={37}
            width={37}
            viewBox="0 0 27 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle
                cx="13.5"
                cy="13.5"
                r="12.5"
                fill="black"
                stroke="black"
                strokeWidth="2"
            />
        </svg>,
    ),
);

export default (selectedSite?: OperatingArea) => {
    // Anchor for where the vehicle icon is
    const VEHICLE_ICON_ANCHOR = useMemo(() => new google.maps.Point(18.5, 11.5), []);
    // Vehicle label details
    const VEHICLE_LABEL_ICON = useMemo(() => ({
        anchor: new google.maps.Point(15, 55),
        url: CIRCLE_IMAGE,
        labelOrigin: new google.maps.Point(18, 20),
    }), []);
    const vehicleLocationAndStatus = useVehicleFirestore(selectedSite);

    const filteredVehiclesWithStatus = vehicleLocationAndStatus.filter(
        ({ status }) => status !== undefined,
    );
    const hasVehicles = filteredVehiclesWithStatus.length > 0;

    const movingMarkers = flatMap(
        vehicleLocationAndStatus,
        ({
            vehicle_id, coordinates, status, pose,
        }) => [
            // The vehicle label (B6S),
            // this must be defined before the vehicle as this contains the click listener
            {
                id: `${vehicle_id}-label`,
                latLng: convertLatLng(coordinates),
                modalText: renderToStaticMarkup(
                    <VehicleModalText vehicleID={vehicle_id} status={status} />,
                ),
                icon: VEHICLE_LABEL_ICON,
                label: {
                    text: splitVehicleID(vehicle_id).toLocaleUpperCase(),
                    color: WHITE,
                    fontWeight: "600",
                    fontFamily: BODY_FONT,
                    fontSize: FONT_0_875,
                },
                zIndex: Z_INDEX_VEHICLE_LABEL,
            },
            // The vehicle marker itself, this is connected via the modalClickLink to the label
            // so they are clicked together
            {
                id: vehicle_id,
                latLng: convertLatLng(coordinates),
                modalClickLink: `${vehicle_id}-label`,
                icon: {
                    anchor: VEHICLE_ICON_ANCHOR,
                    url: VEHICLE_IMAGE(
                        radiansToDegree(pose?.angle || 0),
                        status?.driveGear,
                    ),
                },
                zIndex: Z_INDEX_VEHICLE,
            },
        ],
    );

    const movingMarkerClusterDetails = useMemo(() => ({
        icon: VehicleCluster,
        className: "Cluster_Vehicle",
        // Since the label and vehicles are paired, we want 2 sets (or 4 markers)
        // to be clustered. If we set it to 2 that would cluster the label and vehicle
        minimumClusterSize: 4,
        calculator: defaultMarkerCalculator(2),
    }), []);

    return {
        hasVehicles,
        movingMarkers,
        movingMarkerClusterDetails,
        filteredVehiclesWithStatus,
    };
};
